import { getWorkItemTypeRegistry } from '@/config/work-items/configLoader';
import type { StatusDefinition } from '@/config/work-items/types';
import { isStakeholderWorkflowItemType, getDefaultStakeholderWorkflowStatuses } from './stakeholderStatusConfig';

export interface OperationalWorkflowStatusDefinition {
	key: string;
	label: string;
	color: string;
	description?: string;
	isStart?: boolean;
	isEnd?: boolean;
	isSystem?: boolean;
	allow_billing?: boolean;
}

/** @deprecated Fallback only — SSOT status definitions are loaded from types.yaml via configLoader */
const BASE_OPERATIONAL_WORKFLOW_STATUS_DEFINITIONS: OperationalWorkflowStatusDefinition[] = [
	{
		key: 'open',
		label: 'Unassigned',
		color: 'gray',
		description: 'New items not yet assigned or scheduled',
		isStart: true,
		isSystem: true,
		allow_billing: true,
	},
	{
		key: 'backlog',
		label: 'Backlog',
		color: 'slate',
		description: 'Items queued for future work',
		allow_billing: true,
	},
	{
		key: 'scheduled',
		label: 'Scheduled',
		color: 'cyan',
		description: 'Scheduled for a specific date or time',
		isSystem: true,
		allow_billing: false,
	},
	{
		key: 'dispatched',
		label: 'Dispatched',
		color: 'violet',
		description: 'Dispatched to a technician or team',
		allow_billing: true,
	},
	{
		key: 'en_route',
		label: 'En Route',
		color: 'blue',
		description: 'Travelling to the work location',
		allow_billing: true,
	},
	{
		key: 'arrived',
		label: 'Arrived',
		color: 'indigo',
		description: 'Arrived at the work location',
		allow_billing: true,
	},
	{
		key: 'in_progress',
		label: 'In Progress',
		color: 'yellow',
		description: 'Currently being worked on',
		isSystem: true,
		allow_billing: true,
	},
	{
		key: 'on_hold',
		label: 'On Hold',
		color: 'orange',
		description: 'Work paused or blocked',
		allow_billing: true,
	},
	{
		key: 'pending_approval',
		label: 'Pending Approval',
		color: 'amber',
		description: 'Awaiting admin review or approval',
		allow_billing: true,
	},
	{
		key: 'admin_reopened',
		label: 'Admin Re-opened',
		color: 'blue',
		description: 'Unlocked again by an authorised admin after closure',
		isSystem: true,
		allow_billing: true,
	},
	{
		key: 'resolved',
		label: 'Resolved',
		color: 'green',
		description: 'Operational work completed and resolved',
		isEnd: true,
		isSystem: true,
		allow_billing: true,
	},
	{
		key: 'closed',
		label: 'Closed',
		color: 'emerald',
		description: 'Formally closed after resolution',
		isEnd: true,
		isSystem: true,
		allow_billing: false,
	},
	{
		key: 'cancelled',
		label: 'Cancelled',
		color: 'red',
		description: 'Work cancelled',
		isEnd: true,
		isSystem: true,
		allow_billing: true,
	},
];

/** @deprecated Fallback only — SSOT status definitions are loaded from types.yaml via configLoader */
// Job-specific statuses extend the base operational list with appointment-derived statuses
// that are set automatically when child appointments complete.
const JOB_WORKFLOW_STATUS_DEFINITIONS: OperationalWorkflowStatusDefinition[] = [
	...BASE_OPERATIONAL_WORKFLOW_STATUS_DEFINITIONS.filter(
		(s) => s.key !== 'resolved' && s.key !== 'closed' && s.key !== 'cancelled',
	),
	// Appointment-derived system statuses: set automatically when a child appointment completes
	{
		key: 'apt_resolved',
		label: 'APT Resolved',
		color: 'green',
		description: 'All child appointments resolved successfully',
		isSystem: true,
		allow_billing: true,
	},
	{
		key: 'apt_partial',
		label: 'APT Partial',
		color: 'orange',
		description: 'A child appointment was partially completed',
		isSystem: true,
		allow_billing: true,
	},
	{
		key: 'apt_reschedule',
		label: 'APT Reschedule',
		color: 'amber',
		description: 'A child appointment was rescheduled',
		isSystem: true,
		allow_billing: true,
	},
	{
		key: 'apt_cancelled',
		label: 'APT Cancelled',
		color: 'gray',
		description: 'A child appointment was cancelled',
		isSystem: true,
		allow_billing: true,
	},
	// Billing lifecycle statuses: inserted between apt_* and resolved/closed/cancelled
	{
		key: 'billing_review',
		label: 'Billing Review',
		color: 'amber',
		description: 'All work complete, billing calculations in progress',
		isSystem: true,
		allow_billing: true,
	},
	{
		key: 'billing_submitted',
		label: 'Billing Submitted',
		color: 'teal',
		description: 'Job submitted to finance for billing',
		isSystem: true,
		allow_billing: true,
	},
	...BASE_OPERATIONAL_WORKFLOW_STATUS_DEFINITIONS.filter(
		(s) => s.key === 'resolved' || s.key === 'closed' || s.key === 'cancelled',
	),
];

/** @deprecated Fallback only — SSOT status definitions are loaded from types.yaml via configLoader */
const TICKET_WORKFLOW_STATUS_DEFINITIONS: OperationalWorkflowStatusDefinition[] = [
	{ key: 'open', label: 'Open', color: 'blue', description: 'New ticket', isStart: true, isSystem: true },
	{ key: 'in_progress', label: 'In Progress', color: 'yellow', description: 'Being worked on', isSystem: true },
	{ key: 'waiting', label: 'Waiting', color: 'orange', description: 'Awaiting response or information' },
	{ key: 'resolved', label: 'Resolved', color: 'green', description: 'Issue resolved', isEnd: true, isSystem: true },
	{ key: 'closed', label: 'Closed', color: 'gray', description: 'Ticket closed', isEnd: true, isSystem: true },
];

/** @deprecated Fallback only — SSOT status definitions are loaded from types.yaml via configLoader */
const FOLLOW_UP_WORKFLOW_STATUS_DEFINITIONS: OperationalWorkflowStatusDefinition[] = [
	{ key: 'open', label: 'Open', color: 'blue', description: 'Follow-up raised, not yet actioned', isStart: true, isSystem: true },
	{ key: 'in_progress', label: 'In Progress', color: 'green', description: 'Follow-up is being worked on', isSystem: true },
	{ key: 'waiting', label: 'Waiting on Response', color: 'amber', description: 'Awaiting a response from the customer or assignee' },
	{ key: 'resolved', label: 'Resolved', color: 'gray', description: 'Follow-up resolved', isEnd: true, isSystem: true },
	{ key: 'completed', label: 'Completed', color: 'gray', description: 'Follow-up completed', isEnd: true },
	{ key: 'closed', label: 'Closed', color: 'gray', description: 'Follow-up closed', isEnd: true },
	{ key: 'cancelled', label: 'Cancelled', color: 'gray', description: 'Follow-up cancelled', isEnd: true },
	{ key: 'done', label: 'Done', color: 'gray', description: 'Follow-up done', isEnd: true },
];

/** @deprecated Fallback only — SSOT status definitions are loaded from types.yaml via configLoader */
const MATTER_WORKFLOW_STATUS_DEFINITIONS: OperationalWorkflowStatusDefinition[] = [
	{ key: 'intake', label: 'Intake', color: 'gray', description: 'Initial matter intake', isStart: true, isSystem: true },
	{ key: 'conflict_check', label: 'Conflict Check', color: 'purple', description: 'Checking for conflicts of interest' },
	{ key: 'active', label: 'Active', color: 'blue', description: 'Matter is active' },
	{ key: 'discovery', label: 'Discovery', color: 'cyan', description: 'Discovery phase' },
	{ key: 'negotiation', label: 'Negotiation', color: 'amber', description: 'Negotiation phase' },
	{ key: 'litigation', label: 'Litigation', color: 'red', description: 'In litigation' },
	{ key: 'in_progress', label: 'In Progress', color: 'yellow', description: 'Work in progress', isSystem: true },
	{ key: 'on_hold', label: 'On Hold', color: 'orange', description: 'Matter paused' },
	{ key: 'settled', label: 'Settled', color: 'green', description: 'Matter settled', isEnd: true },
	{ key: 'resolved', label: 'Resolved', color: 'green', description: 'Matter resolved', isEnd: true, isSystem: true },
	{ key: 'closed', label: 'Closed', color: 'emerald', description: 'Matter formally closed', isEnd: true, isSystem: true },
	{ key: 'cancelled', label: 'Cancelled', color: 'red', description: 'Matter cancelled', isEnd: true, isSystem: true },
];

/** @deprecated Fallback only — SSOT status definitions are loaded from types.yaml via configLoader */
const APPOINTMENT_WORKFLOW_STATUS_DEFINITIONS: OperationalWorkflowStatusDefinition[] = [
	{
		key: 'scheduled',
		label: 'Scheduled',
		color: 'blue',
		description: 'Appointment scheduled',
		isStart: true,
		isSystem: true,
	},
	{
		key: 'on_route',
		label: 'On Route / Travelling',
		color: 'violet',
		description: 'Technician is travelling to site',
		isSystem: true,
	},
	{
		key: 'in_progress',
		label: 'In Progress / Working',
		color: 'yellow',
		description: 'Appointment work in progress',
		isSystem: true,
	},
	{
		key: 'paused',
		label: 'Paused',
		color: 'amber',
		description: 'Work temporarily paused',
		isSystem: true,
	},
	{
		key: 'ai_review',
		label: 'AI Review',
		color: 'purple',
		description: 'Flagged by AI for manager review',
		isSystem: true,
	},
	{
		key: 'ai_on_hold',
		label: 'AI On Hold',
		color: 'amber',
		description: 'Held in the AI review workflow pending action',
		isSystem: true,
	},
	{
		key: 'admin_reopened',
		label: 'Admin Re-opened',
		color: 'blue',
		description: 'Unlocked again by an authorised admin after closure',
		isSystem: true,
	},
	{
		key: 'resolved',
		label: 'Resolved',
		color: 'green',
		description: 'Appointment successfully resolved',
		isEnd: true,
		isSystem: true,
	},
	{
		key: 'partial',
		label: 'Partial',
		color: 'orange',
		description: 'Appointment was partially resolved and still needs follow-up',
		isEnd: true,
		isSystem: true,
	},
	{
		key: 'unresolved',
		label: 'Unresolved',
		color: 'orange',
		description: 'Appointment ended without full resolution',
		isEnd: true,
		isSystem: true,
	},
	{
		key: 'rescheduled',
		label: 'Rescheduled',
		color: 'amber',
		description: 'Appointment was rescheduled to a new date or time',
		isEnd: true,
		isSystem: true,
	},
	{
		key: 'closed',
		label: 'Closed',
		color: 'emerald',
		description: 'Appointment formally closed',
		isEnd: true,
		isSystem: true,
	},
	{
		key: 'missed',
		label: 'Missed',
		color: 'red',
		description: 'Appointment was missed',
		isEnd: true,
		isSystem: true,
	},
	{
		key: 'no_show',
		label: 'No Show',
		color: 'red',
		description: 'Customer did not attend',
		isEnd: true,
	},
	{
		key: 'cancelled',
		label: 'Cancelled',
		color: 'gray',
		description: 'Appointment cancelled',
		isEnd: true,
		isSystem: true,
	},
];

/** @deprecated Fallback only — SSOT status definitions are loaded from types.yaml via configLoader */
const VEHICLE_OPERATIONAL_WORKFLOW_STATUS_DEFINITIONS: OperationalWorkflowStatusDefinition[] = [
	{
		key: 'active',
		label: 'Active',
		color: 'emerald',
		description: 'Vehicle is in service and available for operations',
		isStart: true,
	},
	{
		key: 'pending',
		label: 'Pending',
		color: 'amber',
		description: 'Awaiting onboarding, approval, or setup before entering service',
	},
	{
		key: 'repairs',
		label: 'Repairs',
		color: 'orange',
		description: 'Vehicle is undergoing routine or short-term repairs',
	},
	{
		key: 'long_term_repairs',
		label: 'Long Term Repairs',
		color: 'red',
		description: 'Vehicle is off the road for major or long-running repairs',
	},
	{
		key: 'not_running',
		label: 'Not Running',
		color: 'slate',
		description: 'Vehicle is not operational and cannot currently be driven',
	},
	{
		key: 'inactive',
		label: 'Inactive',
		color: 'gray',
		description: 'Vehicle is intentionally out of service or parked for an extended period',
	},
];

const OPERATIONAL_ITEM_TYPES = new Set(['job', 'task', 'project', 'ticket', 'matter']);

const COMPLETION_LIKE_STATUS_KEYS = new Set([
	'completed',
	'resolved',
	'partial',
	'unresolved',
	'rescheduled',
	'done',
	'closed',
	'cancelled',
	'missed',
	'no_show',
	'failed',
	'skipped',
]);

const SUCCESS_STATUS_KEYS = new Set(['completed', 'resolved', 'done', 'closed']);
const NEGATIVE_OUTCOME_STATUS_KEYS = new Set(['partial', 'unresolved', 'rescheduled', 'cancelled', 'missed', 'no_show', 'failed', 'skipped']);
const ACTIVE_WORK_STATUS_KEYS = new Set(['in_progress', 'working', 'checked_in', 'paused']);
const ON_SITE_STATUS_KEYS = new Set(['on_site', 'arrived']);
const ON_ROUTE_STATUS_KEYS = new Set(['on_route', 'en_route']);
const ADMIN_REOPEN_LOCKED_STATUS_KEYS = new Set(['closed']);
const TIMING_OVERLAY_STATUS_KEYS = new Set(['running_late', 'over_schedule']);
/**
 * Statuses that mean "field work is outstanding" for jobs/appointments —
 * not yet touched (new/open/scheduled/dispatched), in flight (on_route/
 * en_route/on_site), being worked (in_progress/checked_in/working/paused),
 * timing overlays (running_late/over_schedule/behind_schedule), or reopened
 * for more work (admin_reopened/on_hold). Terminal appointment outcomes
 * (resolved, partial, unresolved, rescheduled, missed, no_show, cancelled,
 * closed) and billing/admin statuses are deliberately excluded — a
 * rescheduled appointment spawns a NEW appointment on the same job.
 */
export const FIELD_WORK_ACTIVE_STATUS_KEYS = [
	'new',
	'open',
	'assigned',
	'scheduled',
	'dispatched',
	'on_hold',
	'en_route',
	'on_route',
	'arrived',
	'on_site',
	'in_progress',
	'working',
	'checked_in',
	'paused',
	'running_late',
	'behind_schedule',
	'over_schedule',
	'admin_reopened',
] as const;
const FULL_COMPLETION_PROGRESS_STATUS_KEYS = new Set(['completed', 'done', 'closed', 'finished']);
const FOLLOW_UP_PROGRESS_STATUS_KEYS = new Set(['partial', 'unresolved', 'rescheduled']);
const PROTECTED_TERMINAL_APPOINTMENT_STATUS_KEYS = new Set(['missed', 'cancelled', 'no_show']);
const LEGACY_APPOINTMENT_STATUS_KEYS_TO_REMOVE = new Set<string>(['confirmed', 'arrived', 'on_site']);

function cloneStatuses(statuses: OperationalWorkflowStatusDefinition[]): OperationalWorkflowStatusDefinition[] {
	return statuses.map((status) => ({ ...status }));
}

export function normalizeWorkflowStatusKey(status?: string | null): string {
	return typeof status === 'string'
		? status.trim().toLowerCase().replace(/[\s-]+/g, '_')
		: '';
}

export function getDefaultOperationalWorkflowStatuses(): OperationalWorkflowStatusDefinition[] {
	return cloneStatuses(BASE_OPERATIONAL_WORKFLOW_STATUS_DEFINITIONS);
}

export function getDefaultJobWorkflowStatuses(): OperationalWorkflowStatusDefinition[] {
	return cloneStatuses(JOB_WORKFLOW_STATUS_DEFINITIONS);
}

export function getDefaultAppointmentWorkflowStatuses(): OperationalWorkflowStatusDefinition[] {
	return cloneStatuses(APPOINTMENT_WORKFLOW_STATUS_DEFINITIONS);
}

export function getDefaultVehicleOperationalWorkflowStatuses(): OperationalWorkflowStatusDefinition[] {
	return cloneStatuses(VEHICLE_OPERATIONAL_WORKFLOW_STATUS_DEFINITIONS);
}

export function getDefaultTicketWorkflowStatuses(): OperationalWorkflowStatusDefinition[] {
	return cloneStatuses(TICKET_WORKFLOW_STATUS_DEFINITIONS);
}

export function getDefaultFollowUpWorkflowStatuses(): OperationalWorkflowStatusDefinition[] {
	return cloneStatuses(FOLLOW_UP_WORKFLOW_STATUS_DEFINITIONS);
}

export function getDefaultMatterWorkflowStatuses(): OperationalWorkflowStatusDefinition[] {
	return cloneStatuses(MATTER_WORKFLOW_STATUS_DEFINITIONS);
}

export function isVehicleWorkflowItemType(itemType?: string | null, itemSubtype?: string | null): boolean {
	return normalizeWorkflowStatusKey(itemType) === 'asset'
		&& normalizeWorkflowStatusKey(itemSubtype) === 'vehicle';
}

export function getDefaultOperationalWorkflowStatusesForItemType(itemType?: string | null): OperationalWorkflowStatusDefinition[] {
	const normalizedItemType = normalizeWorkflowStatusKey(itemType);

	if (itemType) {
		const configLoader = getWorkItemTypeRegistry();
		const ssotStatuses = configLoader.getStatusesForItemType(normalizedItemType);
		if (ssotStatuses.length > 0) {
			return ssotStatuses.map(s => ({
				key: s.key,
				label: s.label,
				color: s.color,
				description: s.description,
				isSystem: s.is_system || false,
				isStart: s.is_initial || false,
				isEnd: s.is_terminal || false,
			}));
		}
	}

	if (normalizedItemType === 'appointment') {
		return getDefaultAppointmentWorkflowStatuses();
	}

	if (normalizedItemType === 'job') {
		return getDefaultJobWorkflowStatuses();
	}

	if (normalizedItemType === 'ticket') {
		return getDefaultTicketWorkflowStatuses();
	}

	if (normalizedItemType === 'follow_up') {
		return getDefaultFollowUpWorkflowStatuses();
	}

	if (normalizedItemType === 'matter') {
		return getDefaultMatterWorkflowStatuses();
	}

	// Stakeholder item types (customer, supplier, vendor, partner, contractor, contact)
	// use their own workflow statuses tied to the contact account_status field.
	// stakeholderStatusConfig only imports a type from this module, so there is
	// no runtime circular dependency — safe to import directly.
	if (isStakeholderWorkflowItemType(normalizedItemType)) {
		return getDefaultStakeholderWorkflowStatuses();
	}

	return getDefaultOperationalWorkflowStatuses();
}

export function isOperationalWorkflowItemType(itemType?: string | null): boolean {
	const normalizedItemType = normalizeWorkflowStatusKey(itemType);
	return normalizedItemType === 'appointment' || OPERATIONAL_ITEM_TYPES.has(normalizedItemType);
}

export function ensureRequiredOperationalWorkflowStatuses(
	statuses: Array<OperationalWorkflowStatusDefinition> | null | undefined,
	itemType?: string | null,
): OperationalWorkflowStatusDefinition[] {
	const normalizedItemType = normalizeWorkflowStatusKey(itemType);
	const incomingStatuses = Array.isArray(statuses)
		? statuses.map((status) => ({ ...status }))
		: [];
	const normalizedIncomingStatuses = normalizedItemType === 'appointment'
		? incomingStatuses.filter((status) => !LEGACY_APPOINTMENT_STATUS_KEYS_TO_REMOVE.has(normalizeWorkflowStatusKey(status.key)))
		: incomingStatuses;

	if (!isOperationalWorkflowItemType(normalizedItemType)) {
		return normalizedIncomingStatuses;
	}

	if (normalizedIncomingStatuses.length === 0) {
		return getDefaultOperationalWorkflowStatusesForItemType(normalizedItemType);
	}

	const defaultStatuses = getDefaultOperationalWorkflowStatusesForItemType(normalizedItemType);
	const requiredStatuses = defaultStatuses.filter((status) => status.isSystem);
	const requiredStatusKeys = new Set(requiredStatuses.map((status) => normalizeWorkflowStatusKey(status.key)));
	const terminalStatusKeys = new Set(defaultStatuses.filter((status) => status.isEnd).map((status) => normalizeWorkflowStatusKey(status.key)));

	const mergedStatuses = normalizedIncomingStatuses.map((status) => ({
		...status,
		isSystem: requiredStatusKeys.has(normalizeWorkflowStatusKey(status.key)) ? true : status.isSystem,
	}));

	requiredStatuses.forEach((requiredStatus, index) => {
		const requiredKey = normalizeWorkflowStatusKey(requiredStatus.key);
		if (mergedStatuses.some((status) => normalizeWorkflowStatusKey(status.key) === requiredKey)) {
			return;
		}

		const nextExistingDefaultStatus = defaultStatuses
			.slice(index + 1)
			.find((candidate) => mergedStatuses.some((status) => normalizeWorkflowStatusKey(status.key) === normalizeWorkflowStatusKey(candidate.key)));

		const insertIndex = nextExistingDefaultStatus
			? mergedStatuses.findIndex((status) => normalizeWorkflowStatusKey(status.key) === normalizeWorkflowStatusKey(nextExistingDefaultStatus.key))
			: mergedStatuses.findIndex((status) => terminalStatusKeys.has(normalizeWorkflowStatusKey(status.key)));

		mergedStatuses.splice(insertIndex >= 0 ? insertIndex : mergedStatuses.length, 0, {
			...requiredStatus,
			isSystem: requiredStatus.isSystem,
		});
	});

	return mergedStatuses;
}

export function isTerminalWorkItemStatus(status?: string | null, itemType?: string | null): boolean {
	if (itemType) {
		const configLoader = getWorkItemTypeRegistry();
		const result = configLoader.isTerminalStatus(itemType, normalizeWorkflowStatusKey(status));
		if (result) return true;
	}
	return COMPLETION_LIKE_STATUS_KEYS.has(normalizeWorkflowStatusKey(status));
}

export function isAdminReopenLockedStatus(status?: string | null): boolean {
	return ADMIN_REOPEN_LOCKED_STATUS_KEYS.has(normalizeWorkflowStatusKey(status));
}

/**
 * Job statuses that block creating new appointments for the job. Shared by the
 * Appointments tab, Kanban drag/drop appointment setup, and calendar drag
 * appointment creation so the "job closed" rule stays consistent everywhere.
 */
const JOB_STATUS_KEYS_BLOCKING_APPOINTMENT_CREATION = new Set([
	'completed', 'done', 'closed', 'cancelled', 'cancel',
]);

export function jobStatusBlocksAppointmentCreation(status?: string | null): boolean {
	return JOB_STATUS_KEYS_BLOCKING_APPOINTMENT_CREATION.has(normalizeWorkflowStatusKey(status));
}

export function isTimingOverlayStatus(status?: string | null): boolean {
	return TIMING_OVERLAY_STATUS_KEYS.has(normalizeWorkflowStatusKey(status));
}

/**
 * Maps a timing overlay status back to its underlying operational status.
 * `running_late` is an overlay on `scheduled` or `on_route` depending on whether
 * the technician has started travelling. `over_schedule` is an overlay on `in_progress`.
 *
 * Callers that know the timing sub-status (e.g. `onroute_late`) should pass
 * `hint` to get the correct base; otherwise `scheduled` is returned as the
 * conservative default for `running_late`.
 */
export function resolveTimingOverlayToBaseStatus(
	status?: string | null,
	hint?: 'onroute_late' | 'late' | 'over_schedule' | null,
): string | null {
	const normalized = normalizeWorkflowStatusKey(status);

	if (normalized === 'running_late') {
		return hint === 'onroute_late' ? 'on_route' : 'scheduled';
	}

	if (normalized === 'over_schedule') {
		return 'in_progress';
	}

	return null;
}

export function isSuccessfulCompletionStatus(status?: string | null): boolean {
	return SUCCESS_STATUS_KEYS.has(normalizeWorkflowStatusKey(status));
}

export function isNegativeOutcomeStatus(status?: string | null): boolean {
	return NEGATIVE_OUTCOME_STATUS_KEYS.has(normalizeWorkflowStatusKey(status));
}

export function isProtectedTerminalAppointmentStatus(status?: string | null): boolean {
	return PROTECTED_TERMINAL_APPOINTMENT_STATUS_KEYS.has(normalizeWorkflowStatusKey(status));
}

export function isActivelyWorkedStatus(status?: string | null): boolean {
	return ACTIVE_WORK_STATUS_KEYS.has(normalizeWorkflowStatusKey(status));
}

export function isOnSiteWorkItemStatus(status?: string | null): boolean {
	return ON_SITE_STATUS_KEYS.has(normalizeWorkflowStatusKey(status));
}

export function isOnRouteWorkItemStatus(status?: string | null): boolean {
	return ON_ROUTE_STATUS_KEYS.has(normalizeWorkflowStatusKey(status));
}

export function getSuggestedProgressFromStatus(
	status?: string | null,
	currentProgress?: number | null,
): number | null | undefined {
	const normalizedStatus = normalizeWorkflowStatusKey(status);

	if (normalizedStatus === 'admin_reopened') {
		return 100;
	}

	if (normalizedStatus === 'in_progress' && (currentProgress == null || currentProgress === 0)) {
		return 10;
	}

	if (FULL_COMPLETION_PROGRESS_STATUS_KEYS.has(normalizedStatus)) {
		return 100;
	}

	if (normalizedStatus === 'resolved') {
		return 99;
	}

	if (FOLLOW_UP_PROGRESS_STATUS_KEYS.has(normalizedStatus)) {
		return 95;
	}

	if (normalizedStatus === 'cancelled') {
		return 100;
	}

	return currentProgress;
}

export function getSmartTaskVisibleStatuses(itemType: string): StatusDefinition[] {
	const configLoader = getWorkItemTypeRegistry();
	return configLoader.getSmartTaskVisibleStatuses(itemType);
}

export function getAdminSidebarVisibleStatuses(itemType: string): StatusDefinition[] {
	const configLoader = getWorkItemTypeRegistry();
	return configLoader.getAdminSidebarVisibleStatuses(itemType);
}

export function getDataGridVisibleStatuses(itemType: string): StatusDefinition[] {
	const configLoader = getWorkItemTypeRegistry();
	return configLoader.getDataGridVisibleStatuses(itemType);
}

export function getOperationalAllowedTransitions(itemType: string, currentStatus: string): string[] {
	const configLoader = getWorkItemTypeRegistry();
	return configLoader.getAllowedTransitions(itemType, currentStatus);
}
